import { RunContextApp } from "framework/RCApp";
import { 
  ApplicationScopeResponse, 
  ApplicationScopeQueryParams, 
  ApplicationScopeSaveRequest,
  ApplicationScopeSaveResponse,
  FuelTypeAllowance
} from "types/apis/applicationScopeInterfaces";

// Centralized validation functions
const validateScopeData = (scopeData: ApplicationScopeSaveRequest): void => {
  if (!scopeData || typeof scopeData !== 'object') {
    throw new Error('Scope data must be an object');
  }

  if (!scopeData.project_id) {
    throw new Error('Missing project_id');
  }

  if (!Array.isArray(scopeData.applications) || scopeData.applications.length === 0) {
    throw new Error('Applications must be a non-empty array');
  }

  scopeData.applications.forEach((app, index) => {
    if (!app.master_data_id || typeof app.master_data_id !== 'number') {
      throw new Error(`Invalid master_data_id at index ${index}`);
    }
    
    if (!app.allowed_for || typeof app.allowed_for !== 'object') {
      throw new Error(`Missing or invalid allowed_for at index ${index}`);
    }
    
    // Validate allowed_for structure
    const requiredFields = ['electric', 'gas', 'water', 'dual_fuel'] as const;
    requiredFields.forEach(field => {
      if (typeof app.allowed_for[field] !== 'boolean') {
        throw new Error(`Invalid ${field} value at index ${index}: must be boolean`);
      }
    });

    if ('api' in app.allowed_for && typeof (app.allowed_for as FuelTypeAllowance).api !== 'boolean') {
      throw new Error(`Invalid api value at index ${index}: must be boolean`);
    }
    if ('non_api' in app.allowed_for && typeof (app.allowed_for as FuelTypeAllowance).non_api !== 'boolean') {
      throw new Error(`Invalid non_api value at index ${index}: must be boolean`);
    }
  });
};

/**
 * Application Scope API methods - following the pilotAPI pattern
 */
export const ApplicationScopeApis = {
  
  /**
   * Get application scope data based on user attributes
   */
  getApplicationScope: async (
    rc: RunContextApp,
    queryParams: ApplicationScopeQueryParams
  ): Promise<ApplicationScopeResponse> => {
    try {
      console.log('Fetching application scope with params:', queryParams);
      
      const response = await rc.bffClient.getApplicationScope(queryParams);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          data: { applications: [], allowed_measurement_type: [] },
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response && response.data) {
        console.log('Successfully fetched application scope data:', response.data);
        return {
          success: true,
          data: response.data,
          error: undefined
        };
      }
      
      return {
        success: true,
        data: { applications: [], allowed_measurement_type: [] },
        error: undefined
      };
    } catch (error) {
      rc.error(rc.getName(ApplicationScopeApis), 
        `Error >> GET request to : /v1/application-scope\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        data: { applications: [], allowed_measurement_type: [] },
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Save application scope data
   */
  saveApplicationScope: async (
    rc: RunContextApp,
    scopeData: ApplicationScopeSaveRequest
  ): Promise<{ success: boolean; data?: ApplicationScopeSaveResponse; error?: string }> => {
    try {
      // Validate scope data
      validateScopeData(scopeData);
      
      console.log('Saving application scope data:', scopeData);
      
      const response = await rc.bffClient.saveApplicationScope(scopeData);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response && response.data) {
        console.log('Successfully saved application scope data:', response.data);
        return {
          success: true,
          data: response.data,
          error: undefined
        };
      }
      
      return {
        success: true,
        error: undefined
      };
    } catch (error) {
      rc.error(rc.getName(ApplicationScopeApis), 
        `Error >> POST request to : /v1/application-scope/save\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
};

// Export backward compatibility functions
export const getApplicationScope = async (
  rc: RunContextApp,
  queryParams: ApplicationScopeQueryParams
): Promise<ApplicationScopeResponse> => {
  return ApplicationScopeApis.getApplicationScope(rc, queryParams);
};

export const saveApplicationScope = async (
  rc: RunContextApp,
  scopeData: ApplicationScopeSaveRequest
): Promise<ApplicationScopeSaveResponse> => {
  const result = await ApplicationScopeApis.saveApplicationScope(rc, scopeData);

  if (result.success && result.data) {
    return result.data;
  } else {
    const err = result.error;
    const errorMsg =
      typeof err === 'string'
        ? err
        : (err as any)?.message || 'Failed to save application scope data';
    throw new Error(errorMsg);
  }
};